import React from 'react'
import PropTypes from 'prop-types'
import {
    AccordionButton,
    Box,
    Image,
    useMultiStyleConfig,
    Text,
    AccordionPanel,
    Button,
    Accordion,
    AccordionItem,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getUrl} from '../../../utils/cloudinary-utils'
import {FormattedMessage, useIntl} from 'react-intl'
import EGiftCardFields from '../../../components/forms/giftcard-fields'
import {KS_PRIVACY_POLICY_URL, KS_TERMS_AND_CONFITIONS_URL} from '../../../constants'
import {useGlobaleContext} from '../../../contexts/globale'
import useTagManager from '../../../hooks/use-tag-manager'

const CardForm = ({submitForm, product, showAddToCartLoader, showAddedCta, form}) => {
    const tagManager = useTagManager()
    const intl = useIntl()
    const {countryISO} = useGlobaleContext()
    const geRestricted = countryISO && countryISO !== 'US'
    const styles = useMultiStyleConfig('GiftCertificate')
    const renderGERestrictedText = ({colorVariant = 'black'}) => {
        return (
            <Box
                borderColor={colorVariant === 'black' ? 'black' : 'white'}
                color={colorVariant === 'black' ? 'black' : 'white'}
                {...styles.rightBox.globaleNotAvailable}
            >
                {intl.formatMessage({
                    defaultMessage: 'This Product Is Not Eligible For Shipment Outside Of The USA.',
                    id: 'product_view.button.ge_forbidden'
                })}
            </Box>
        )
    }

    const onValid = async (data) => {
        return submitForm(data)
    }

    const onInvalid = (errors) => {
        Object.values(errors).forEach((error) => {
            if (error?.message) {
                tagManager.errorDisplayed('form validation', error.message, 'giftcard form')
            }
        })
    }
    return (
        <form
            id="egift-card-form"
            name="egift-card-form"
            onSubmit={form.handleSubmit(onValid, onInvalid)}
            data-testid="sf-egift-card-form"
        >
            <Stack {...styles.rightBox.form.main}>
                <Stack {...styles.rightBox.form.fields.main}>
                    <EGiftCardFields form={form} />
                </Stack>
                {product?.c_eGiftCardDetailText && (
                    <Accordion variant="accordion" allowMultiple={true} defaultIndex={[0]}>
                        <Box
                            key="details"
                            id="pro-details-tab"
                            className="accordion-wrapper"
                            borderBottomWidth="1px"
                            sx={{
                                '.email-instruction': {
                                    width: '100%'
                                },
                                span: {
                                    whiteSpace: 'nowrap'
                                }
                            }}
                        >
                            <AccordionItem display="grid">
                                {({isExpanded}) => (
                                    <>
                                        <AccordionButton
                                            paddingRight={0}
                                            {...styles.rightBox.accordionBtn.main}
                                        >
                                            <Text
                                                as="span"
                                                itemProp="name"
                                                {...styles.rightBox.accordionBtn.text}
                                            >
                                                <FormattedMessage
                                                    id="eGiftCard.delivery"
                                                    defaultMessage="eGift Card Delivery"
                                                />
                                            </Text>

                                            <>
                                                {isExpanded ? (
                                                    <Image
                                                        alt="Expand Icon"
                                                        width="15px"
                                                        height="15px"
                                                        color="black"
                                                        verticalAlign="middle"
                                                        src={getUrl(
                                                            'UIImages/images/minus-large.svg'
                                                        )}
                                                    />
                                                ) : (
                                                    <Image
                                                        alt="Expand Icon"
                                                        width="15px"
                                                        height="15px"
                                                        color="black"
                                                        verticalAlign="middle"
                                                        src={getUrl(
                                                            'UIImages/images/plus-large.svg'
                                                        )}
                                                    />
                                                )}
                                            </>
                                        </AccordionButton>

                                        <AccordionPanel paddingBottom="30px">
                                            <Box
                                                itemProp="text"
                                                sx={{
                                                    a: {
                                                        textDecoration: 'underline',
                                                        _hover: {
                                                            textDecoration: 'none'
                                                        }
                                                    }
                                                }}
                                            >
                                                <Box
                                                    {...styles.rightBox.instructionsText}
                                                    dangerouslySetInnerHTML={{
                                                        __html: product?.c_eGiftCardDetailText
                                                    }}
                                                />
                                            </Box>
                                        </AccordionPanel>
                                    </>
                                )}
                            </AccordionItem>
                        </Box>
                    </Accordion>
                )}
                <Text {...styles.rightBox.termsConditions}>
                    <FormattedMessage
                        defaultMessage="By clicking Add to Bag, you agree to Kendra Scott's <a>{Terms}</a> and <a>{Privacy}</a>."
                        values={{
                            Privacy: 'Privacy Policy',
                            Terms: 'Terms & Conditions',
                            a: (chunks) => (
                                <a
                                    target="_blank"
                                    href={KS_TERMS_AND_CONFITIONS_URL}
                                    rel="noreferrer"
                                >
                                    {chunks}
                                </a>
                            ),
                            b: (chunks) => (
                                <a target="_blank" href={KS_PRIVACY_POLICY_URL} rel="noreferrer">
                                    {chunks}
                                </a>
                            )
                        }}
                        id="accepting.terms.and.conditions"
                    />
                </Text>
                {!geRestricted && (
                    <Button {...styles.rightBox.addToBag} isLoading={showAddToCartLoader}>
                        {showAddedCta ? (
                            <FormattedMessage defaultMessage="Added" id="added.to.cart" />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Add to Bag"
                                id="product_view.button.add_to_cart"
                            />
                        )}
                    </Button>
                )}
                {geRestricted && (
                    <Box className="ge-restricted">
                        {renderGERestrictedText({colorVariant: 'black'})}
                    </Box>
                )}
                <Box {...styles.rightBox.textBelowAddToBag}>
                    {product?.c_eGiftCardNotEligilible && (
                        <Box
                            {...styles.rightBox.notEligibleText}
                            dangerouslySetInnerHTML={{
                                __html: product?.c_eGiftCardNotEligilible
                            }}
                        />
                    )}
                    <Text {...styles.rightBox.finalSalesText}>
                        <FormattedMessage
                            id="final.sales"
                            defaultMessage="Final Sale. No returns or exchanges."
                        />
                    </Text>
                </Box>
            </Stack>
        </form>
    )
}

CardForm.propTypes = {
    product: PropTypes.object,
    submitForm: PropTypes.func,
    showAddToCartLoader: PropTypes.bool,
    form: PropTypes.any,
    showAddedCta: PropTypes.bool
}

export default CardForm
